/**
 * Строка цели — вариант «Кольцо» (выбор владельца 14.10.2026,
 * docs/design/goals-2026-10-14).
 *
 * Устроена как любая строка приложения: значок, название, одна серая
 * строка, сумма справа. Прогресс — кольцом вокруг значка, без отдельной
 * полоски и процента: «25 000 ₽ из 140 000» говорит то же числами. Значок —
 * состояние: копилка — копим, пауза — на паузе, галочка — собрано; у
 * подушки — зонт.
 *
 * Одна и та же строка — на главной («Коплю») и в «Деньги → Цели»; там ниже
 * ещё счёт, на котором копим.
 */
import { ZERO, bufferView, formatMoney, goalEta, monthlyForDeadline, type Goal, type Money, type PlainDate } from '@skoka/core';
import type { AppState } from './store.js';
import { Icon } from './icon.js';
import { Tip } from './tip.js';
import { AccountGlyph } from './account-glyph.js';
import { goalAccountId } from './goal-account.js';
import { dayMonthYear } from './text.js';

const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
/** «к фев 2027» — срок коротко: в серой строке место дорого. */
const byMonth = (d: PlainDate): string => `к ${MONTHS_SHORT[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
const pctOf = (g: Goal): number => (g.targetAmount > ZERO ? Math.min(100, Math.round((g.currentAmount / g.targetAmount) * 100)) : 0);

/** Кольцо вокруг значка цели: прогресс фирменным градиентом, на паузе — серым. */
function Ring({ goal }: { goal: Goal }) {
  const r = 19;
  const c = 2 * Math.PI * r;
  const pct = pctOf(goal);
  const paused = goal.status === 'paused';
  const done = goal.status === 'achieved' || pct >= 100;
  const icon = done ? 'check' : paused ? 'pause' : goal.kind === 'emergency_fund' ? 'umbrella' : 'bullseye-arrow';
  const color = paused ? 'gray' : goal.kind === 'emergency_fund' ? 'blue' : 'green';
  return (
    <span className="goalring" aria-hidden="true">
      <svg viewBox="0 0 44 44" width="44" height="44">
        <circle className="goalring-track" cx="22" cy="22" r={r} />
        {pct > 0 && (
          <circle className={`goalring-on${paused ? ' paused' : ''}`} cx="22" cy="22" r={r}
            strokeDasharray={`${(c * pct) / 100} ${c}`} transform="rotate(-90 22 22)" />
        )}
      </svg>
      <span className={`catico c-${color} s-r`}><Icon name={icon} size={14} /></span>
    </span>
  );
}

/**
 * «Новая цель» — последней строкой списка целей (16.10.2026,
 * docs/design/goals-2026-10-16): маленький «+» в углу заголовка не находили.
 * Строка как у цели, только кольцо пунктиром — место для новой.
 */
export function NewGoalRow({ hint, onAdd }: { hint: string; onAdd: () => void }) {
  return (
    <button className="li w goalrow goalnew" onClick={onAdd}>
      <span className="goalring" aria-hidden="true">
        <svg viewBox="0 0 44 44" width="44" height="44"><circle className="goalring-new" cx="22" cy="22" r="19" /></svg>
        <span className="catico c-gray s-r"><Icon name="plus" size={14} /></span>
      </span>
      <div className="nm"><b>Новая цель</b><span className="goalsub">{hint}</span></div>
      <span className="ch"><Icon name="chevron-right" size={13} /></span>
    </button>
  );
}

/** Взнос словами: назначенный, посчитанный к сроку или честное «без взноса». */
export function goalMonthly(goal: Goal, today: PlainDate): string {
  if (goal.status === 'achieved') return 'собрано';
  if (goal.status === 'paused') return 'на паузе';
  if (goal.monthlyContribution > ZERO) return `${formatMoney(goal.monthlyContribution)}/мес`;
  if (goal.targetDate) {
    const need = monthlyForDeadline(goal, goal.targetDate, today);
    if (need > ZERO) return `≈ ${formatMoney(need)}/мес`;
  }
  return 'без взноса';
}

/** Когда соберётся: названный срок, иначе расчёт по взносу. Нет взноса — не знаем. */
export function goalWhen(goal: Goal, today: PlainDate): PlainDate | null {
  if (goal.status !== 'active') return null;
  if (goal.targetDate) return goal.targetDate;
  return goal.monthlyContribution > ZERO ? goalEta(goal, goal.monthlyContribution, today) : null;
}

export function GoalRow({ state, goal, needFloor, detail = false, onOpen }: {
  state: AppState;
  goal: Goal;
  /** Месяц расходов — платежи и повседневные нужды: от него считается запас подушки. */
  needFloor: Money;
  /** В «Деньги → Цели»: ещё строка со счётом. */
  detail?: boolean;
  onOpen: () => void;
}) {
  const monthly = goalMonthly(goal, state.today);
  const quiet = monthly === 'без взноса' || monthly === 'на паузе';
  const when = goalWhen(goal, state.today);
  const isBuffer = goal.kind === 'emergency_fund' && goal.status === 'active' && needFloor > ZERO;
  const bv = isBuffer ? bufferView(goal.currentAmount, needFloor) : null;
  const accId = detail ? goalAccountId(state, goal) : null;
  const account = accId ? state.accounts.find((a) => a.id === accId) : undefined;
  return (
    <button className={`li w goalrow${goal.status === 'paused' ? ' paused' : ''}${goal.status === 'achieved' ? ' done' : ''}`} onClick={onOpen}>
      <Ring goal={goal} />
      <div className="nm">
        <b>{goal.name}</b>
        {/* Куски строки не рвутся: не влезло — срок уходит на вторую строку целиком. */}
        <span className="goalsub">
          <span className={`seg${quiet ? ' quiet' : ''}`}>{monthly}</span>
          {!bv && when ? <> <span className="seg">· {byMonth(when)}</span></> : null}
        </span>
        {/* Подушка — ещё строкой: на сколько месяцев хватит, с расчётом в (i). В
            одну строку со взносом не помещается и наезжала на сумму справа. */}
        {bv && (
          <span>запас на {String(bv.months).replace('.', ',')} мес
            <Tip>
              Месяц расходов — обязательные платежи и повседневные нужды: <b>{formatMoney(needFloor)}</b>.{' '}
              {formatMoney(goal.currentAmount)} ÷ {formatMoney(needFloor)} ≈ <b>{String(bv.months).replace('.', ',')} мес</b>.
              Спокойная подушка — 3 месяца, полная — 6.
              {when ? ` По ${monthly.replace('/мес', ' в месяц')} соберётся к ${dayMonthYear(when)}.` : ''}
            </Tip>
          </span>
        )}
        {account && (
          <span className="goalacc"><AccountGlyph account={account} size="f" /><span>{account.name}</span></span>
        )}
      </div>
      <div className="am num">
        {formatMoney(goal.currentAmount)}
        <small>из {formatMoney(goal.targetAmount)}</small>
      </div>
    </button>
  );
}
